import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { getDebts, Debt, DebtsResp, moneyEntero as money } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

// DESFASADOS DEL MES (Mabel 3-nov-2026): «al darle clic a desfasados [en el Inicio] cuando me mande al
// reporte de adeudos, que también aparezca la listita de desfasados por separado del mes».
// El Inicio solo enseña cada desfasado 3 días antes de su fecha; aquí van TODOS los del mes —los que
// faltan, los que ya vencieron y los ya pagados— para verlos de un vistazo. Solo lectura: cobrar y
// cancelar siguen en la lista general de abajo (los desfasados siguen ahí también, sin cambiar totales).
// Pide `include_settled=1` (el mismo GET /finances/debts) para poder enseñar los «Pagado».
const DIAS_ANTES = 3;
type Estado = 'proximo' | 'por_vencer' | 'vencido' | 'pagado' | 'cancelado';
const ESTADO: Record<Estado, { label: string; cls: string }> = {
  proximo: { label: 'Próximo', cls: 'bg-surface-2 text-content-2' },
  por_vencer: { label: 'Por vencer', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  vencido: { label: 'Vencido', cls: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  pagado: { label: 'Pagado', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  cancelado: { label: 'Cancelado', cls: 'bg-surface-2 text-content-3 line-through' },
};
type DebtX = Debt & { cancelled_at?: string | null };
function estadoDe(d: DebtX): Estado {
  if (d.settled) return d.cancelled_at ? 'cancelado' : 'pagado';
  const dias = d.days_to_due == null ? null : Number(d.days_to_due);
  if (dias == null) return 'proximo';
  if (dias < 0) return 'vencido';
  return dias <= DIAS_ANTES ? 'por_vencer' : 'proximo';
}
const ymHoy = () => { const h = new Date(); return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`; };
function moverMes(ym: string, delta: number) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function nombreMes(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const opts: Intl.DateTimeFormatOptions = { month: 'long' };
  if (y !== new Date().getFullYear()) opts.year = 'numeric';
  return new Date(y, m - 1, 1).toLocaleDateString('es-MX', opts);
}
/** 'YYYY-MM-DD' → «sáb 7 nov» (sin desfase de zona). */
function fechaCobro(ymd: string | null) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
}
function detalleDias(d: DebtX, e: Estado) {
  if (e === 'pagado' || e === 'cancelado' || d.days_to_due == null) return null;
  const n = Number(d.days_to_due);
  return n < 0 ? `hace ${-n} día${n === -1 ? '' : 's'}` : n === 0 ? 'hoy' : `en ${n} día${n === 1 ? '' : 's'}`;
}

type Props = { refreshKey?: number; resaltar?: boolean };

const DesfasadosMes = forwardRef<HTMLDivElement, Props>(function DesfasadosMes({ refreshKey = 0, resaltar = false }, ref) {
  const KEY = 'fin-debts:all';
  const [resp, setResp] = useState<DebtsResp | null>(() => readCache<DebtsResp>(KEY) ?? null);
  const [mes, setMes] = useState(ymHoy);

  useEffect(() => {
    getDebts(true)
      .then((r) => { setResp(r); writeCache(KEY, r); })
      .catch(() => setResp((prev) => prev ?? { data: [], summary: { total_open: 0, count_open: 0, students_open: 0 } }));
  }, [refreshKey]);

  // El mes del desfasado es el de su FECHA DE COBRO (due_date); `month` como respaldo si no la trae.
  const filas = useMemo(() => {
    if (!resp) return [];
    return (resp.data as DebtX[])
      .filter((d) => d.due_rule_applied === 'desfasado' && (d.due_date || d.month || '').slice(0, 7) === mes)
      .sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
  }, [resp, mes]);

  const pendiente = filas.filter((d) => !d.settled).reduce((acc, d) => acc + (Number(d.remaining ?? d.amount) || 0), 0);
  const cobrado = filas.filter((d) => d.settled && !d.cancelled_at).reduce((acc, d) => acc + (Number(d.amount) || 0), 0);

  return (
    <div ref={ref} id="desfasados" data-testid="desfasados-mes"
      className={`scroll-mt-4 rounded-2xl border bg-surface p-4 shadow-sm transition-shadow duration-700 sm:p-5 ${resaltar ? 'border-amber-500/60 ring-2 ring-amber-500/40' : 'border-line'}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-base font-bold text-content">
          <CalendarClock size={18} className="text-amber-600 dark:text-amber-400" />
          Desfasados de {nombreMes(mes)}
        </h3>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setMes((m) => moverMes(m, -1))} aria-label="Mes anterior"
            className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><ChevronLeft size={16} /></button>
          {mes !== ymHoy() && (
            <button type="button" onClick={() => setMes(ymHoy())}
              className="rounded-lg px-2 py-1 text-xs font-medium text-brand-blue hover:underline">Este mes</button>
          )}
          <button type="button" onClick={() => setMes((m) => moverMes(m, 1))} aria-label="Mes siguiente"
            className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><ChevronRight size={16} /></button>
        </div>
      </div>

      {resp === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : filas.length === 0 ? (
        <p className="py-3 text-center text-sm text-content-3">Nadie tiene mensualidad desfasada en {nombreMes(mes)}.</p>
      ) : (
        <>
          <p className="mb-2 text-xs text-content-3">
            {filas.length} alumna{filas.length === 1 ? '' : 's'} · por cobrar <b className="text-amber-600 dark:text-amber-400">{money(pendiente)}</b>
            {cobrado > 0 && <> · ya pagado <b className="text-emerald-600 dark:text-emerald-400">{money(cobrado)}</b></>}
            {' '}· en el Inicio cada una sale {DIAS_ANTES} días antes de su fecha.
          </p>
          <ul className="divide-y divide-line">
            {filas.map((d) => {
              const e = estadoDe(d);
              const dias = detalleDias(d, e);
              const abono = !d.settled && Number(d.paid_amount) > 0;
              return (
                <li key={d.debt_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid="desfasado-mes-fila">
                  <span className="flex min-w-0 flex-1 basis-48 items-center gap-2.5">
                    <PersonAvatar contactId={d.contact_id} hasPhoto={d.has_photo && !!d.contact_id} name={d.student_name} size="h-8 w-8" />
                    {d.contact_id
                      ? <Link to={`/alumnos/${d.contact_id}`} className="truncate text-sm font-medium text-content hover:text-accent-ink">{d.student_name}</Link>
                      : <span className="truncate text-sm font-medium text-content">{d.student_name}</span>}
                  </span>
                  <span className="w-36 text-sm text-content-2">
                    {fechaCobro(d.due_date)}
                    {dias && <span className={`block text-[11px] ${e === 'vencido' ? 'font-medium text-red-600 dark:text-red-400' : 'text-content-3'}`}>{dias}</span>}
                  </span>
                  <span className="w-24 text-right text-sm font-semibold tabular-nums text-content">
                    {money(d.amount)}
                    {abono && <span className="block text-[11px] font-normal text-content-3">resta {money(d.remaining)}</span>}
                  </span>
                  <span className={`w-24 shrink-0 rounded-full px-2 py-0.5 text-center text-[11px] font-medium ${ESTADO[e].cls}`} data-testid="desfasado-mes-estado">
                    {ESTADO[e].label}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
});

export default DesfasadosMes;
